import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CORSTroubleshootingGuide = ({ bucketName, region, onClose }) => {
  const [copiedSection, setCopiedSection] = useState('');

  const corsPolicy = `[
  {
    "AllowedOrigins": ["*"],
    "AllowedMethods": ["GET", "PUT", "POST", "DELETE", "HEAD"],
    "AllowedHeaders": ["*"],
    "ExposeHeaders": ["ETag", "x-amz-server-side-encryption", "x-amz-request-id", "x-amz-id-2"],
    "MaxAgeSeconds": 3000
  }
]`;

  const handleCopy = (text, section) => {
    navigator.clipboard?.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(''), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto bg-card border-2 border-destructive rounded-lg shadow-lg">
      <div className="p-6 border-b border-border bg-destructive/10">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-destructive/20 rounded-lg">
              <Icon name="AlertCircle" size={28} className="text-destructive" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">CORS Configuration Required</h2>
              <p className="text-muted-foreground">
                Your S3 bucket <span className="font-mono font-semibold text-foreground">{bucketName}</span> needs CORS configuration to allow browser access
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Close guide"
            >
              <Icon name="X" size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="bg-primary/10 border border-primary rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm text-foreground">
              <p className="font-semibold mb-1">Why is this happening?</p>
              <p>
                Web browsers block direct access to S3 buckets for security reasons unless CORS (Cross-Origin Resource Sharing) is configured.
                This is a standard security measure to protect users from malicious websites.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Settings" size={20} className="text-primary" />
            Step-by-Step Fix
          </h3>

          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Open AWS S3 Console</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Navigate to your S3 bucket in the AWS Console
                  </p>
                  <a
                    href={`https://s3.console.aws.amazon.com/s3/buckets/${bucketName}?region=${region}&tab=permissions`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Icon name="ExternalLink" size={16} />
                    Open {bucketName} in AWS Console
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Navigate to Permissions Tab</h4>
                  <p className="text-sm text-muted-foreground">
                    Click on the "Permissions" tab in your bucket settings
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Scroll to CORS Configuration</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Find the "Cross-origin resource sharing (CORS)" section and click "Edit"
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                  4
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Add CORS Configuration</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Copy and paste this CORS policy into the configuration editor:
                  </p>
                  <div className="relative">
                    <pre className="bg-background border border-border rounded-lg p-4 overflow-x-auto text-sm font-mono text-foreground">
                      {corsPolicy}
                    </pre>
                    <button
                      onClick={() => handleCopy(corsPolicy, 'cors')}
                      className="absolute top-2 right-2 p-2 bg-card hover:bg-muted border border-border rounded-lg transition-colors"
                      aria-label="Copy CORS policy"
                    >
                      <Icon 
                        name={copiedSection === 'cors' ? 'Check' : 'Copy'} 
                        size={16} 
                        className={copiedSection === 'cors' ? 'text-green-500' : 'text-muted-foreground'}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                  5
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Save Changes</h4>
                  <p className="text-sm text-muted-foreground">
                    Click "Save changes" at the bottom of the CORS configuration editor
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                  6
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Refresh and Test</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Return to this page and refresh to test the connection
                  </p>
                  <Button
                    onClick={() => window.location?.reload()}
                    iconName="RefreshCw"
                    iconPosition="left"
                    variant="outline"
                  >
                    Refresh Page
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-amber-500/10 border border-amber-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-foreground">
              <p className="font-semibold mb-1">Important Notes</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>This CORS policy allows all origins (*) for development. For production, restrict to your domain.</li>
                <li>Changes may take a few seconds to propagate across AWS servers.</li>
                <li>Make sure your IAM credentials have proper permissions for the bucket.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="HelpCircle" size={18} className="text-primary" />
            Still Having Issues?
          </h4>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>If you're still experiencing problems after following these steps:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Verify your bucket name and region are correct in Settings</li>
              <li>Check that your AWS credentials have ListBucket permission</li>
              <li>Ensure your bucket's Block Public Access settings allow CORS</li>
              <li>Try clearing your browser cache and cookies</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CORSTroubleshootingGuide;